'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { trackRegistration } from '@/lib/analytics';
import { useAuth } from '@/contexts/AuthContext';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: string;
  password: string;
  confirmPassword: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  emergencyContact: string;
  emergencyPhone: string;
  medicalHistory: string;
  allergies: string;
  currentMedications: string;
  termsAccepted: boolean;
  hipaaAccepted: boolean;
  marketingOptIn: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const RegistrationForm = () => {
  const router = useRouter();
  const { signUp } = useAuth();
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    emergencyContact: '',
    emergencyPhone: '',
    medicalHistory: '',
    allergies: '',
    currentMedications: '',
    termsAccepted: false,
    hipaaAccepted: false,
    marketingOptIn: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (formData.password) {
      calculatePasswordStrength(formData.password);
    }
  }, [formData.password]);

  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
    setPasswordStrength(Math.min(strength, 100));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData.gender) newErrors.gender = 'Gender is required';
    }

    if (step === 2) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.state) newErrors.state = 'State is required';
      if (!formData.zipCode.trim()) {
        newErrors.zipCode = 'ZIP code is required';
      } else if (!/^\d{5}$/.test(formData.zipCode)) {
        newErrors.zipCode = 'Please enter a valid 5-digit ZIP code';
      }
    }

    if (step === 3) {
      if (!formData.emergencyContact.trim()) newErrors.emergencyContact = 'Emergency contact name is required';
      if (!formData.emergencyPhone.trim()) {
        newErrors.emergencyPhone = 'Emergency contact phone is required';
      } else if (!/^\d{10}$/.test(formData.emergencyPhone.replace(/\D/g, ''))) {
        newErrors.emergencyPhone = 'Please enter a valid 10-digit phone number';
      }
    }

    if (step === 4) {
      if (!formData.termsAccepted) newErrors.termsAccepted = 'You must accept the Terms of Service';
      if (!formData.hipaaAccepted) newErrors.hipaaAccepted = 'You must accept the HIPAA Privacy Notice';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (currentStep < 4) {
      handleNext();
      return;
    }

    if (!validateStep(4)) return;

    setIsSubmitting(true);

    try {
      // Create user in Supabase with all profile metadata
      await signUp(formData.email, formData.password, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        date_of_birth: formData.dateOfBirth,
        gender: formData.gender,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
      });

      // Track registration conversion
      trackRegistration({
        userId: formData.email,
        method: 'email',
      });

      // Redirect to login with success message
      router.push('/login?registered=true');
    } catch (error: any) {
      console.error('Registration error:', error);
      setErrors({ submit: error?.message || 'Registration failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return 'bg-error';
    if (passwordStrength < 70) return 'bg-warning';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };

  if (!isHydrated) {
    return (
      <div className="w-full max-w-3xl mx-auto bg-card rounded-lg shadow-elevation-md p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-3/4"></div>
          <div className="space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { number: 1, title: 'Personal Information', icon: 'UserIcon' },
    { number: 2, title: 'Account & Address', icon: 'HomeIcon' },
    { number: 3, title: 'Medical Information', icon: 'HeartIcon' },
    { number: 4, title: 'Review & Consent', icon: 'DocumentCheckIcon' }
  ];

  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-smooth ${
                    currentStep >= step.number
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {currentStep > step.number ? (
                    <Icon name="CheckIcon" size={24} />
                  ) : (
                    <Icon name={step.icon as any} size={24} />
                  )}
                </div>
                <p className={`text-xs mt-2 text-center hidden sm:block ${
                  currentStep >= step.number ? 'text-foreground font-medium' : 'text-muted-foreground'
                }`}>
                  {step.title}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`h-1 flex-1 mx-2 transition-smooth ${
                  currentStep > step.number ? 'bg-primary' : 'bg-muted'
                }`}></div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card rounded-lg shadow-elevation-md p-6 sm:p-8">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                  Personal Information
                </h2>
                <p className="text-muted-foreground">
                  Let's start with your basic information
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-md border ${
                      errors.firstName ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                    placeholder="John"
                  />
                  {errors.firstName && (
                    <p className="text-error text-sm mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.firstName}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-md border ${
                      errors.lastName ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                    placeholder="Doe"
                  />
                  {errors.lastName && (
                    <p className="text-error text-sm mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-md border ${
                    errors.email ? 'border-error' : 'border-input'
                  } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                  placeholder="john.doe@example.com"
                />
                {errors.email && (
                  <p className="text-error text-sm mt-1 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-md border ${
                    errors.phone ? 'border-error' : 'border-input'
                  } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                  placeholder="(555) 123-4567"
                />
                {errors.phone && (
                  <p className="text-error text-sm mt-1 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.phone}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-foreground mb-2">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-md border ${
                      errors.dateOfBirth ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                  />
                  {errors.dateOfBirth && (
                    <p className="text-error text-sm mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.dateOfBirth}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-foreground mb-2">
                    Gender *
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-md border ${
                      errors.gender ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                  {errors.gender && (
                    <p className="text-error text-sm mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.gender}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                  Account & Address
                </h2>
                <p className="text-muted-foreground">
                  Create your secure account and provide your address
                </p>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 pr-12 rounded-md border ${
                      errors.password ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-smooth"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
                  </button>
                </div>
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-smooth ${getPasswordStrengthColor()}`}
                          style={{ width: `${passwordStrength}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">
                        {getPasswordStrengthText()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Use 8+ characters with mix of letters, numbers & symbols
                    </p>
                  </div>
                )}
                {errors.password && (
                  <p className="text-error text-sm mt-1 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.password}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 pr-12 rounded-md border ${
                      errors.confirmPassword ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                    placeholder="Re-enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-smooth"
                  >
                    <Icon name={showConfirmPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-error text-sm mt-1 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-foreground mb-2">
                  Street Address *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-md border ${
                    errors.address ? 'border-error' : 'border-input'
                  } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                  placeholder="123 Main Street, Apt 4B"
                />
                {errors.address && (
                  <p className="text-error text-sm mt-1 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.address}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-foreground mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-md border ${
                      errors.city ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                    placeholder="New York"
                  />
                  {errors.city && (
                    <p className="text-error text-sm mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.city}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-foreground mb-2">
                    State *
                  </label>
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-md border ${
                      errors.state ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                  >
                    <option value="">Select</option>
                    {usStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  {errors.state && (
                    <p className="text-error text-sm mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.state}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-foreground mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-md border ${
                      errors.zipCode ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                    placeholder="10001"
                    maxLength={5}
                  />
                  {errors.zipCode && (
                    <p className="text-error text-sm mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.zipCode}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                  Medical Information
                </h2>
                <p className="text-muted-foreground">
                  Help us provide better care with your medical background
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="emergencyContact" className="block text-sm font-medium text-foreground mb-2">
                    Emergency Contact Name *
                  </label>
                  <input
                    type="text"
                    id="emergencyContact"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-md border ${
                      errors.emergencyContact ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                    placeholder="Jane Doe"
                  />
                  {errors.emergencyContact && (
                    <p className="text-error text-sm mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.emergencyContact}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="emergencyPhone" className="block text-sm font-medium text-foreground mb-2">
                    Emergency Contact Phone *
                  </label>
                  <input
                    type="tel"
                    id="emergencyPhone"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-md border ${
                      errors.emergencyPhone ? 'border-error' : 'border-input'
                    } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth`}
                    placeholder="(555) 987-6543"
                  />
                  {errors.emergencyPhone && (
                    <p className="text-error text-sm mt-1 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {errors.emergencyPhone}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="medicalHistory" className="block text-sm font-medium text-foreground mb-2">
                  Medical History (Optional)
                </label>
                <textarea
                  id="medicalHistory"
                  name="medicalHistory"
                  value={formData.medicalHistory}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth resize-none"
                  placeholder="Please list any significant medical conditions, surgeries, or chronic illnesses..."
                ></textarea>
                <p className="text-xs text-muted-foreground mt-1">
                  This information helps our healthcare providers give you better care
                </p>
              </div>

              <div>
                <label htmlFor="allergies" className="block text-sm font-medium text-foreground mb-2">
                  Known Allergies (Optional)
                </label>
                <textarea
                  id="allergies"
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth resize-none"
                  placeholder="List any medication, food, or environmental allergies..."
                ></textarea>
              </div>

              <div>
                <label htmlFor="currentMedications" className="block text-sm font-medium text-foreground mb-2">
                  Current Medications (Optional)
                </label>
                <textarea
                  id="currentMedications"
                  name="currentMedications"
                  value={formData.currentMedications}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth resize-none"
                  placeholder="List all medications you're currently taking, including dosages..."
                ></textarea>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex gap-3">
                  <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0" />
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-1">
                      Why We Need This Information
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Your medical history helps our healthcare providers make informed decisions about your treatment. All information is kept confidential and secure under HIPAA regulations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                  Review & Consent
                </h2>
                <p className="text-muted-foreground">
                  Please review your information and accept our terms
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                <div>
                  <h3 className="font-heading font-semibold text-foreground mb-2">Personal Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>
                      <span className="ml-2 text-foreground font-medium">
                        {formData.firstName} {formData.lastName}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email:</span>
                      <span className="ml-2 text-foreground font-medium">{formData.email}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Phone:</span>
                      <span className="ml-2 text-foreground font-medium">{formData.phone}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date of Birth:</span>
                      <span className="ml-2 text-foreground font-medium">{formData.dateOfBirth}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <h3 className="font-heading font-semibold text-foreground mb-2">Address</h3>
                  <p className="text-sm text-foreground">
                    {formData.address}<br />
                    {formData.city}, {formData.state} {formData.zipCode}
                  </p>
                </div>

                <div className="border-t border-border pt-4">
                  <h3 className="font-heading font-semibold text-foreground mb-2">Emergency Contact</h3>
                  <p className="text-sm text-foreground">
                    {formData.emergencyContact} - {formData.emergencyPhone}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className={`flex items-start gap-3 p-4 rounded-lg border ${
                  errors.termsAccepted ? 'border-error bg-error/5' : 'border-border'
                }`}>
                  <input
                    type="checkbox"
                    id="termsAccepted"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleInputChange}
                    className="mt-1 w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary cursor-pointer"
                  />
                  <label htmlFor="termsAccepted" className="text-sm text-foreground cursor-pointer">
                    I accept the <a href="#" className="text-primary hover:underline font-medium">Terms of Service</a> and <a href="#" className="text-primary hover:underline font-medium">Privacy Policy</a>
                  </label>
                </div>
                {errors.termsAccepted && (
                  <p className="text-error text-sm flex items-center gap-1 -mt-2">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.termsAccepted}
                  </p>
                )}

                <div className={`flex items-start gap-3 p-4 rounded-lg border ${
                  errors.hipaaAccepted ? 'border-error bg-error/5' : 'border-border'
                }`}>
                  <input
                    type="checkbox"
                    id="hipaaAccepted"
                    name="hipaaAccepted"
                    checked={formData.hipaaAccepted}
                    onChange={handleInputChange}
                    className="mt-1 w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary cursor-pointer"
                  />
                  <label htmlFor="hipaaAccepted" className="text-sm text-foreground cursor-pointer">
                    I acknowledge that I have read and understand the <a href="#" className="text-primary hover:underline font-medium">HIPAA Privacy Notice</a> and consent to the use and disclosure of my health information as described
                  </label>
                </div>
                {errors.hipaaAccepted && (
                  <p className="text-error text-sm flex items-center gap-1 -mt-2">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.hipaaAccepted}
                  </p>
                )}

                <div className="flex items-start gap-3 p-4 rounded-lg border border-border">
                  <input
                    type="checkbox"
                    id="marketingOptIn"
                    name="marketingOptIn"
                    checked={formData.marketingOptIn}
                    onChange={handleInputChange}
                    className="mt-1 w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary cursor-pointer"
                  />
                  <label htmlFor="marketingOptIn" className="text-sm text-foreground cursor-pointer">
                    I would like to receive health tips, appointment reminders, and promotional offers via email and SMS (Optional)
                  </label>
                </div>
              </div>

              <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                <div className="flex gap-3">
                  <Icon name="ShieldCheckIcon" size={24} className="text-success flex-shrink-0" />
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-1">
                      Your Data is Protected
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      We use bank-level encryption to protect your personal and medical information. Your data is stored securely and never shared without your explicit consent.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={handlePrevious}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-3 text-foreground font-medium transition-smooth hover:bg-muted rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="ChevronLeftIcon" size={20} />
                Previous
              </button>
            ) : (
              <div></div>
            )}

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-smooth hover:shadow-elevation-md hover:scale-[0.97] active:scale-[0.95] ml-auto"
              >
                Next
                <Icon name="ChevronRightIcon" size={20} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-smooth hover:shadow-elevation-md hover:scale-[0.97] active:scale-[0.95] disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account
                    <Icon name="CheckIcon" size={20} />
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationForm;